/**
 * Btn — botão primitivo.
 *
 * 5 variants (primary, ghost, outline, danger, subtle) e 3 sizes (sm, md, lg).
 * Visual Sphere: raio 8, altura mínima por tamanho (alvo de toque), brilho
 * localizado só no primary. As cores vêm do tema (`C`) — lidas a cada render,
 * então a troca de tema (remount) também vale para os botões.
 *
 * Acessibilidade: foco visível pelo reset global (NavaStyles); aria-disabled
 * quando disabled=true; role="button" implícito via <button>.
 */
import { C } from '@/theme/palette';

const SIZES = {
  sm: { px: 12, h: 34, fs: 13 },
  md: { px: 16, h: 40, fs: 14 },
  lg: { px: 22, h: 46, fs: 15 },
};

function variantStyle(variant) {
  switch (variant) {
    case 'ghost':
      return { backgroundColor: `${C.blackElev}CC`, color: C.neutral, border: `1px solid ${C.blackBorder}` };
    case 'outline':
      return { backgroundColor: `${C.black}88`, color: C.cyber, border: `1px solid ${C.cyber}99` };
    case 'danger':
      return { backgroundColor: 'transparent', color: C.hot, border: `1px solid ${C.hot}99` };
    case 'subtle':
      return { backgroundColor: C.blackElev2, color: C.neutral, border: `1px solid ${C.blackBorder}` };
    case 'primary':
    default:
      return {
        backgroundColor: C.cyber, color: C.black, border: `1px solid ${C.cyber}`,
        boxShadow: `0 0 18px ${C.cyber}33`,
      };
  }
}

export function Btn({
  children,
  variant = 'primary',
  onClick,
  icon: Icon,
  disabled = false,
  size = 'md',
  type = 'button',
  title,
  'aria-label': ariaLabel,
}) {
  const s = SIZES[size] ?? SIZES.md;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled}
      aria-label={ariaLabel}
      title={title}
      style={{
        ...variantStyle(variant),
        minHeight: s.h,
        paddingLeft: s.px,
        paddingRight: s.px,
        paddingTop: 0,
        paddingBottom: 0,
        fontSize: s.fs,
        fontWeight: 550,
        lineHeight: 1.2,
        borderRadius: 8,
        whiteSpace: 'nowrap',
        opacity: disabled ? 0.48 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background-color 140ms, box-shadow 140ms, filter 140ms, transform 140ms',
      }}
      className="nava-btn inline-flex items-center justify-center gap-2"
    >
      {Icon && <Icon size={s.fs + 2} strokeWidth={1.75} aria-hidden="true" />}
      {children}
    </button>
  );
}
